import { FC, useState } from 'react';
import { z } from 'zod';
import { DocumentType, graphql } from '../../../gql';
import { useApiQuery } from '../../../query/hooks';
import { MapToNewMetric } from './mappingModalComponents/MapToNewMetric';
import { Box, DialogTitle, Divider, Theme, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { MapToExistingMetric } from './mappingModalComponents/MapToExistingMetric';

const getUnmappedSpreadsheetSheets = graphql(`
	query getUnmappedSpreadsheetSheetsQuery($spreadsheetId: String!) {
		googleSpreadsheetSheets(spreadsheetId: $spreadsheetId) {
			sheets {
				sheetTitle
				templateId
				templateName
			}
		}
	}
`);

export const mapUnmappedSheetsModalSchema = z.object({
	spreadsheetExternalId: z.string(),
});

type Props = z.infer<typeof mapUnmappedSheetsModalSchema>;

export type UnmappedSheets = NonNullable<DocumentType<typeof getUnmappedSpreadsheetSheets>['googleSpreadsheetSheets']>['sheets'];

type MetricMappingVariant = 'new' | 'existing';

export const MapUnmappedSheetsModal: FC<Props> = ({ spreadsheetExternalId }) => {
	const [activeItem, setActiveItem] = useState<MetricMappingVariant>('new');
	const intl = useIntl();
	const { data, isLoading } = useApiQuery(getUnmappedSpreadsheetSheets, { variables: { spreadsheetId: spreadsheetExternalId } });

	const unmappedSheets = data?.googleSpreadsheetSheets?.sheets.filter((sheet) => sheet?.templateId == null) ?? [];

	return (
		<>
			<DialogTitle>
				<FormattedMessage id={'map-unmapped-sheets-modal.title'} />
			</DialogTitle>
			<Divider />
			<Box px={3} pt={2}>
				<ToggleButtonGroup
					value={activeItem}
					sx={{ alignItems: 'stretch' }}
					fullWidth
					exclusive
					onChange={(_, value) => setActiveItem(value)}
				>
					<ToggleButton sx={(theme) => toggleButtonStyle(theme)} value={'new'}>
						{intl.formatMessage({ id: 'map-unmapped-sheets-modal.variant.new' })}
					</ToggleButton>
					<ToggleButton sx={(theme) => toggleButtonStyle(theme)} value={'existing'}>
						{intl.formatMessage({ id: 'map-unmapped-sheets-modal.variant.existing' })}
					</ToggleButton>
				</ToggleButtonGroup>
			</Box>
			{activeItem === 'new' && (
				<MapToNewMetric unmappedSheets={unmappedSheets} isLoading={isLoading} spreadsheetExternalId={spreadsheetExternalId} />
			)}
			{activeItem === 'existing' && (
				<MapToExistingMetric unmappedSheets={unmappedSheets} isLoading={isLoading} spreadsheetExternalId={spreadsheetExternalId} />
			)}
		</>
	);
};

const toggleButtonStyle = (theme: Theme) => ({
	color: theme.palette.text.primary,
	outline: `1px solid ${theme.palette.grey[300]}`,
	outlineOffset: -1,
	fontSize: {
		xs: theme.typography.subtitle1.fontSize,
		lg: theme.typography.subtitle1.fontSize,
	},
	lineHeight: 1.5,
	fontWeight: 500,
	transition: 'all 0.2s ease',
	'&.Mui-selected': {
		backgroundColor: theme.palette.grey[800],
		color: theme.palette.grey[0],
		border: 'none',
		outline: 'none',
	},
	'&.Mui-selected:hover': {
		backgroundColor: theme.palette.grey[600],
	},
	'&.Mui-selected.MuiToggleButtonGroup-grouped:not(:first-of-type)': {
		borderColor: 'unset',
	},
});
